import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined"
import EastRoundedIcon from "@mui/icons-material/EastRounded"
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"
import StoreMallDirectoryOutlinedIcon from "@mui/icons-material/StoreMallDirectoryOutlined"
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined"
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AiMessageSteps } from "__generated__/graphql"
import { memo, useMemo, useState } from "react"

import {
  ReadableBlock,
  ReadableContext,
  ReadableEvidence,
  ReadableExchange,
  readStep,
  StepActor,
  TranscriptItem,
  TranscriptSpeaker
} from "./stepReadable.utils"

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"

type ActorMeta = {
  label: string
  color: string
  Icon: typeof DnsOutlinedIcon
}

const ACTORS: Record<StepActor, ActorMeta> = {
  host: { label: "Qpien", color: "#5e6d79", Icon: DnsOutlinedIcon },
  model: { label: "AI modeli", color: "#1f5fa8", Icon: SmartToyOutlinedIcon },
  shop: {
    label: "Mağaza (MCP)",
    color: "#286477",
    Icon: StorefrontOutlinedIcon
  },
  knowledge: {
    label: "Bilgi tabanı",
    color: "#866b2b",
    Icon: MenuBookOutlinedIcon
  },
  skill: {
    label: "Skill motoru",
    color: "#76528a",
    Icon: AccountTreeOutlinedIcon
  },
  external: { label: "Dış servis", color: "#9a5d35", Icon: PublicOutlinedIcon },
  marketplace: {
    label: "Pazaryeri API",
    color: "#9a3412",
    Icon: StoreMallDirectoryOutlinedIcon
  },
  customer: { label: "Müşteri", color: "#2e7d32", Icon: PersonOutlineIcon }
}

const SPEAKER_COLORS: Record<TranscriptSpeaker, string> = {
  customer: ACTORS.customer.color,
  host: ACTORS.host.color,
  model: ACTORS.model.color,
  tool: ACTORS.shop.color
}

const AVAILABILITY_LABELS: Record<string, string> = {
  IN_STOCK: "stokta",
  OUT_OF_STOCK: "tükendi",
  LOW_STOCK: "az kaldı",
  PREORDER: "ön sipariş",
  DISCONTINUED: "satıştan kalktı"
}

const NOTICE_TONES = {
  error: { fg: "#b71c1c", bg: "rgba(211, 47, 47, 0.07)" },
  expected: { fg: "#a15c00", bg: "rgba(237, 108, 2, 0.08)" },
  info: { fg: "#37474f", bg: "rgba(96, 125, 139, 0.09)" }
}

const RECORD_PREVIEW = 8
const EVIDENCE_PREVIEW = 5
const CLAMP_CHARS = 240

const copy = (value: string) => {
  void navigator.clipboard?.writeText(value).catch(() => undefined)
}

function ActorPill({ actor }: { actor: StepActor }) {
  const { label, color, Icon } = ACTORS[actor]
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        color,
        bgcolor: alpha(color, 0.1),
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: "nowrap"
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
      {label}
    </Box>
  )
}

function BlockLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontWeight: 700,
        mb: 0.25
      }}
    >
      {children}
    </Typography>
  )
}

function MoreButton({
  open,
  onClick,
  closedLabel
}: {
  open: boolean
  onClick: () => void
  closedLabel: string
}) {
  return (
    <Button
      size="small"
      onClick={onClick}
      sx={{ p: 0, minWidth: 0, fontSize: 11.5, textTransform: "none" }}
    >
      {open ? "daralt" : closedLabel}
    </Button>
  )
}

function ClampText({
  text,
  lines = 3,
  mono = false
}: {
  text: string
  lines?: number
  mono?: boolean
}) {
  const [open, setOpen] = useState(false)
  const long = text.length > CLAMP_CHARS || text.split("\n").length > lines

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          ...(mono ? { fontFamily: MONO, fontSize: 12 } : {}),
          ...(long && !open
            ? {
                display: "-webkit-box",
                WebkitLineClamp: lines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }
            : {})
        }}
      >
        {text}
      </Typography>
      {long && (
        <MoreButton
          open={open}
          onClick={() => setOpen(!open)}
          closedLabel="devamı"
        />
      )}
    </Box>
  )
}

/** Prompts and descriptions run to thousands of characters: closed until asked for. */
function LongText({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false)
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <BlockLabel>{label}</BlockLabel>
        <MoreButton
          open={open}
          onClick={() => setOpen(!open)}
          closedLabel={`göster · ${text.length.toLocaleString("tr-TR")} karakter`}
        />
        {open && (
          <Tooltip title="Metni kopyala" arrow>
            <IconButton
              size="small"
              onClick={() => copy(text)}
              sx={{ p: 0.25 }}
            >
              <ContentCopyIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {open && (
        <Box
          sx={{
            mt: 0.25,
            p: 1,
            maxHeight: 360,
            overflowY: "auto",
            borderRadius: 1,
            bgcolor: "grey.100",
            fontFamily: MONO,
            fontSize: 12,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere"
          }}
        >
          {text}
        </Box>
      )}
    </Box>
  )
}

function TextBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "text" }>
}) {
  if (block.collapsed) return <LongText label={block.label} text={block.text} />
  if (!block.emphasis) {
    return (
      <Box>
        <BlockLabel>{block.label}</BlockLabel>
        <ClampText text={block.text} />
      </Box>
    )
  }
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        borderLeft: 3,
        borderColor: "primary.main",
        bgcolor: "#e5f0fd"
      }}
    >
      <BlockLabel>{block.label}</BlockLabel>
      <Typography
        variant="body2"
        sx={{ color: "#000", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >
        {block.text}
      </Typography>
    </Box>
  )
}

function FieldsBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "fields" }>
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        gap: "2px 12px"
      }}
    >
      {block.rows.map((row) => (
        <Box key={row.label} sx={{ display: "contents" }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", pt: "2px" }}
          >
            {row.label}
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function ListBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "list" }>
}) {
  return (
    <Box>
      <BlockLabel>{block.label}</BlockLabel>
      {block.inline ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {block.items.map((item, index) => (
            <Chip
              key={`${item}-${index}`}
              label={item}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
          ))}
        </Box>
      ) : (
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2,
            display: "flex",
            flexDirection: "column",
            gap: 0.25
          }}
        >
          {block.items.map((item, index) => (
            <Box component="li" key={`${item}-${index}`}>
              <ClampText text={item} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

function RecordsBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "records" }>
}) {
  const [all, setAll] = useState(false)
  const shown = all ? block.rows : block.rows.slice(0, RECORD_PREVIEW)
  const firstTitle = block.rows[0]?.title
  const sameTitle =
    block.rows.length > 1 && block.rows.every((row) => row.title === firstTitle)

  return (
    <Box>
      <BlockLabel>
        {block.label} · {block.total}
      </BlockLabel>
      {sameTitle && (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
          {firstTitle}
        </Typography>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden"
        }}
      >
        {shown.map((row, index) => {
          const availability = row.availability
            ? (AVAILABILITY_LABELS[row.availability] ?? row.availability)
            : ""
          const soldOut = row.availability === "OUT_OF_STOCK"
          return (
            <Box
              key={`${row.title}-${index}`}
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                px: 1,
                py: 0.5,
                borderTop: index ? 1 : 0,
                borderColor: "divider",
                fontSize: 12.5
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {!sameTitle && (
                  <Typography variant="body2" sx={{ fontSize: 12.5 }}>
                    {row.title}
                  </Typography>
                )}
                {row.options && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: sameTitle ? "text.primary" : "text.secondary"
                    }}
                  >
                    {row.options}
                  </Typography>
                )}
              </Box>
              {row.price && (
                <Typography
                  variant="caption"
                  sx={{ fontFamily: MONO, flexShrink: 0 }}
                >
                  {row.price}
                </Typography>
              )}
              {availability && (
                <Typography
                  variant="caption"
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    color: soldOut ? "error.main" : "success.main"
                  }}
                >
                  {availability}
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>
      {block.rows.length > RECORD_PREVIEW && (
        <MoreButton
          open={all}
          onClick={() => setAll(!all)}
          closedLabel={`+${block.rows.length - RECORD_PREVIEW} kayıt daha`}
        />
      )}
    </Box>
  )
}

function EvidenceItem({ item }: { item: ReadableEvidence }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.75,
        borderLeft: 3,
        borderColor: alpha(ACTORS.knowledge.color, 0.5),
        bgcolor: alpha(ACTORS.knowledge.color, 0.04),
        borderRadius: "0 4px 4px 0"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            flex: 1,
            minWidth: 0,
            overflowWrap: "anywhere"
          }}
        >
          {item.title}
        </Typography>
        {item.tags?.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{ height: 16, fontSize: 10 }}
          />
        ))}
        {item.score !== undefined && (
          <Tooltip title="Benzerlik skoru — yüksek olan daha alakalı" arrow>
            <Typography
              variant="caption"
              sx={{ fontFamily: MONO, color: "text.secondary", flexShrink: 0 }}
            >
              {item.score.toFixed(3)}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {item.text && <ClampText text={item.text} />}
    </Box>
  )
}

function EvidenceBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "evidence" }>
}) {
  const [all, setAll] = useState(false)
  const shown = all ? block.items : block.items.slice(0, EVIDENCE_PREVIEW)
  return (
    <Box>
      <BlockLabel>{block.label}</BlockLabel>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {shown.map((item, index) => (
          <EvidenceItem key={`${item.title}-${index}`} item={item} />
        ))}
      </Box>
      {block.items.length > EVIDENCE_PREVIEW && (
        <MoreButton
          open={all}
          onClick={() => setAll(!all)}
          closedLabel={`+${block.items.length - EVIDENCE_PREVIEW} parça daha`}
        />
      )}
    </Box>
  )
}

function TranscriptLine({ item }: { item: TranscriptItem }) {
  const color = SPEAKER_COLORS[item.speaker]
  return (
    <Box
      sx={{
        pl: 1,
        py: 0.5,
        borderLeft: 3,
        borderColor: alpha(color, 0.55)
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 700, color }}
      >
        {item.label}
      </Typography>
      <ClampText text={item.text} lines={item.speaker === "customer" ? 6 : 3} />
    </Box>
  )
}

function TranscriptBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "transcript" }>
}) {
  return (
    <Box>
      <BlockLabel>{block.label}</BlockLabel>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {block.items.map((item, index) => (
          <TranscriptLine key={index} item={item} />
        ))}
      </Box>
    </Box>
  )
}

function NoticeBlock({
  block
}: {
  block: Extract<ReadableBlock, { kind: "notice" }>
}) {
  const tone = NOTICE_TONES[block.tone]
  return (
    <Typography
      variant="body2"
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        color: tone.fg,
        bgcolor: tone.bg,
        overflowWrap: "anywhere"
      }}
    >
      {block.text}
    </Typography>
  )
}

function BlockView({ block }: { block: ReadableBlock }) {
  switch (block.kind) {
    case "text":
      return <TextBlock block={block} />
    case "fields":
      return <FieldsBlock block={block} />
    case "list":
      return <ListBlock block={block} />
    case "records":
      return <RecordsBlock block={block} />
    case "evidence":
      return <EvidenceBlock block={block} />
    case "transcript":
      return <TranscriptBlock block={block} />
    case "notice":
      return <NoticeBlock block={block} />
  }
}

function ExchangeCard({ exchange }: { exchange: ReadableExchange }) {
  const { color } = ACTORS[exchange.from]
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(color, 0.05),
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <ActorPill actor={exchange.from} />
        {exchange.to !== exchange.from && (
          <>
            <EastRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
            <ActorPill actor={exchange.to} />
          </>
        )}
      </Box>
      <Box
        sx={{
          px: 1.25,
          py: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
        >
          {exchange.headline}
        </Typography>
        {exchange.blocks.map((block, index) => (
          <BlockView key={`${block.kind}-${index}`} block={block} />
        ))}
      </Box>
    </Box>
  )
}

function StepReadableView({
  step,
  context
}: {
  step: AiMessageSteps
  context: ReadableContext
}) {
  const exchanges = useMemo(() => readStep(step, context), [step, context])
  if (!exchanges) return null

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
      {exchanges.map((exchange, index) => (
        <ExchangeCard key={index} exchange={exchange} />
      ))}
    </Box>
  )
}

export default memo(StepReadableView)
